import { Input, Loader, SettingsForm } from "@southfields-digital/mxmz-react-ui";
import { useContext, useEffect, useState } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom";

import ResolutionPicker, { PRESET_RESOLUTIONS } from "src/components/ResolutionPicker";
import { BaseLayoutContext, type BaseLayoutContextType } from "src/layouts/BaseLayout";
import menuGroups from "src/pages/Settings/menuGroups";
import { isValidResolution } from "src/utils";

import styles from "./CanvasFormPage.module.scss";

type CanvasFormPageProps = {
	createCanvas: (name: string, width: number, height: number, navigate: NavigateFunction) => void;
	submitting: boolean;
};

export default function CanvasFormPage({ createCanvas, submitting }: CanvasFormPageProps) {
	const navigate = useNavigate();
	const [name, setName] = useState<string>("");
	const [width, setWidth] = useState(PRESET_RESOLUTIONS[0].width);
	const [height, setHeight] = useState(PRESET_RESOLUTIONS[0].height);
	const { setWithContainer } = useContext<BaseLayoutContextType | null>(
		BaseLayoutContext,
	) as BaseLayoutContextType;

	const trimmedName = name.trim();
	const canSubmit = !!trimmedName.length && !submitting && isValidResolution({ width, height });

	useEffect(() => {
		setWithContainer(true);

		return function unmount() {
			setWithContainer(false);
		};
	}, []);

	return (
		<SettingsForm
			title="Create canvas"
			formSections={[
				{
					actions: [
						{
							children: <>{submitting && <Loader className={styles.Spinner} />} Create canvas</>,
							className: styles.CanvasFormSubmitButton,
							disabled: !canSubmit,
							onClick: () => createCanvas(trimmedName, width, height, navigate),
						},
					],
					type: "form",
					form: (
						<div className="flex flex-col gap-5">
							<Input
								type="text"
								value={name}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
								onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
									if (e.key === "Enter") {
										e.preventDefault();

										if (canSubmit) {
											createCanvas(trimmedName, width, height, navigate);
										}
									}
								}}
								placeholder="Pick a recognisable name for your canvas"
							/>
							<div className="flex flex-col gap-1">
								<span>Resolution</span>
								<ResolutionPicker
									selectedPreset={
										PRESET_RESOLUTIONS.find((r) => r.width === width && r.height === height) ?? null
									}
									presets={PRESET_RESOLUTIONS}
									resolution={{ width, height }}
									onChange={(w, h) => {
										setWidth(w);
										setHeight(h);
									}}
								/>
							</div>
						</div>
					),
				},
			]}
			menuGroups={menuGroups}
		/>
	);
}
